import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { SetMaintenanceStatus, SetRefreshRequiredStatus } from '../store/app/app.actions';
import { EnvService } from './env.service';
import { HttpWrapperService } from './api';
import { SentryService } from './sentry.service';
import { mapHttpErrorToCcsResponseError } from '../store/utils';
import { AppState } from '../store/app/app.state';

const semver = require('semver');
const appVersion: string = require('package.json').version;

interface VersionUpdates {
  checkPeriodInMinutes: number;
  latestVersion: string;
  shouldUpdate: boolean;
  hardRefresh: boolean;
  updateMessageTitle: string;
  updateMessage: string;
  updateActionLabel: string;
  isInMaintenance: boolean;
  maintenanceMessageTitle: string;
  maintenanceMessage: string;
}

@Injectable({ providedIn: 'root' })
export class VersionUpdatesService {
  timeout: number = this.envService.versionUpdates.checkPeriodInMinutes;
  timer: NodeJS.Timeout | null = null;
  versionUpdates: VersionUpdates | null = null;

  constructor(
    private store: Store,
    private http: HttpWrapperService,
    private envService: EnvService,
    private sentry: SentryService
  ) {}

  public initVersionCheck() {
    this.initTimer(this.timeout);

    this.checkVersion();
  }

  private initTimer(timeout: number) {
    if (this.timer) clearInterval(this.timer);

    this.timeout = timeout;

    this.timer = setInterval(() => {
      this.checkVersion();
    }, this.timeout * 60 * 1000);
  }

  private checkVersion() {
    this.http.get(`assets/version-2.json?t=${moment().unix()}`).subscribe({
      next: (response: any) => {
        this.versionUpdates = response as VersionUpdates;

        // restart interval with new timeout if changed
        if (this.versionUpdates.checkPeriodInMinutes !== this.timeout) {
          this.initTimer(this.versionUpdates.checkPeriodInMinutes);
        }

        // handle maintenance
        const wasInMaintenance = this.store.selectSnapshot(AppState.isInMaintenance);
        const isInMaintenance = this.versionUpdates.isInMaintenance;
        if (wasInMaintenance !== isInMaintenance) {
          this.store.dispatch(new SetMaintenanceStatus(this.versionUpdates.isInMaintenance)).subscribe(() => {
            if (isInMaintenance) {
              window.open('./maintenance', '_self');
            } else {
              window.open('./', '_self');
            }
          });
        }

        // handle updates
        const lastRefreshed = this.store.selectSnapshot(AppState.lastRefreshed);
        if (
          semver.gt(this.versionUpdates.latestVersion, appVersion) &&
          this.versionUpdates.shouldUpdate &&
          (!lastRefreshed || moment().diff(moment.unix(lastRefreshed), 'minutes') >= this.timeout)
        ) {
          if (this.versionUpdates.hardRefresh) {
            this.store.dispatch(new SetRefreshRequiredStatus(false)).subscribe(() => {
              window.open('./', '_self');
            });
          } else {
            this.store.dispatch(new SetRefreshRequiredStatus(true));
          }
        }
      },
      error: (error) => {
        this.sentry.recordError(mapHttpErrorToCcsResponseError(error));
      },
    });
  }
}
